import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../components/Button';
import { ArrowLeft, Plus, X, Upload, Image as ImageIcon } from 'lucide-react';
import { useAudience } from '../../../context/AudienceContext';
import { createSimulation, handleApiError } from '../../../utils/simulationApi';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface ConceptTestFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
}

interface Segment {
  id: number;
  name: string;
  description: string;
  len: number;
  created_at: string;
  updated_at: string;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const ConceptTestForm: React.FC<ConceptTestFormProps> = ({
  onSubmit,
  selectedSegmentIds,
  personaFilters,
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {}
}) => {
  const { audienceData } = useAudience();
  const [segments, setSegments] = useState<Segment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [headline, setHeadline] = useState(initialFormData.headline || '');
  const [consumerInsight, setConsumerInsight] = useState(initialFormData.consumerInsight || '');
  const [mainBenefit, setMainBenefit] = useState(initialFormData.mainBenefit || '');
  const [reasonsTobelieve, setReasonsTobelieve] = useState<string[]>(
    initialFormData.reasonsTobelieve || ['']
  );
  const [pricePoint, setPricePoint] = useState(initialFormData.pricePoint || '');
  const [packSize, setPackSize] = useState(initialFormData.packSize || '');
  const [brand, setBrand] = useState(initialFormData.brand || '');
  const [heroImageBase64, setHeroImageBase64] = useState<string | null>(
    initialFormData.heroImageBase64 || null
  );
  const [heroImageDescription, setHeroImageDescription] = useState(
    initialFormData.heroImageDescription || ''
  );
  const [heroImagePreview, setHeroImagePreview] = useState<string | null>(
    initialFormData.heroImagePreview || null
  );
  const [alternativeProducts, setAlternativeProducts] = useState<string[]>(
    initialFormData.alternativeProducts || ['']
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update parent with form data changes
  useEffect(() => {
    const formData = {
      simName,
      headline,
      consumerInsight,
      mainBenefit,
      reasonsTobelieve,
      pricePoint,
      packSize,
      brand,
      heroImageBase64,
      heroImageDescription,
      heroImagePreview,
      alternativeProducts
    };
    onFormDataChange(formData);
  }, [
    simName, headline, consumerInsight, mainBenefit, reasonsTobelieve,
    pricePoint, packSize, brand, heroImageBase64, heroImageDescription,
    heroImagePreview, alternativeProducts
  ]);

  // Notify parent of form edits
  const handleFormChange = () => {
    if (onEditStep) {
      onEditStep();
    }
  };

  // Fetch segments
  useEffect(() => {
    const fetchSegments = async () => {
      if (!audienceData.audienceId) return;

      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/audience/${audienceData.audienceId}/segments`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch segments');
        }

        const data = await response.json();
        const filteredSegments = data.filter((segment: Segment) =>
          selectedSegmentIds.includes(segment.id)
        );
        setSegments(filteredSegments);
        setError(null);
      } catch (err) {
        console.error('Error fetching segments:', err);
        setError('Failed to load segments. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSegments();
  }, [audienceData.audienceId, selectedSegmentIds]);

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setHeroImageBase64(base64);
      setHeroImagePreview(base64);
      handleFormChange();
    };
    reader.readAsDataURL(file);
  };

  const removeHeroImage = () => {
    setHeroImageBase64(null);
    setHeroImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    handleFormChange();
  };

  // RTB list management
  const addRTB = () => {
    if (reasonsTobelieve.length < 5) {
      setReasonsTobelieve([...reasonsTobelieve, '']);
      handleFormChange();
    }
  };

  const removeRTB = (index: number) => {
    if (reasonsTobelieve.length > 1) {
      setReasonsTobelieve(reasonsTobelieve.filter((_, i) => i !== index));
      handleFormChange();
    }
  };

  const updateRTB = (index: number, value: string) => {
    const updated = [...reasonsTobelieve];
    updated[index] = value;
    setReasonsTobelieve(updated);
    handleFormChange();
  };

  // Alternative products list management
  const addAlternativeProduct = () => {
    if (alternativeProducts.length < 5) {
      setAlternativeProducts([...alternativeProducts, '']);
      handleFormChange();
    }
  };

  const removeAlternativeProduct = (index: number) => {
    if (alternativeProducts.length > 1) {
      setAlternativeProducts(alternativeProducts.filter((_, i) => i !== index));
      handleFormChange();
    }
  };

  const updateAlternativeProduct = (index: number, value: string) => {
    const updated = [...alternativeProducts];
    updated[index] = value;
    setAlternativeProducts(updated);
    handleFormChange();
  };

  // Validation - require either hero image or description
  const hasHeroImageOrDescription = heroImageBase64 || heroImageDescription.trim() !== '';
  const hasValidRTBs = reasonsTobelieve.some(rtb => rtb.trim() !== '');
  const hasValidAlternatives = alternativeProducts.some(alt => alt.trim() !== '');

  const isFormValid =
    selectedSegmentIds.length > 0 &&
    headline.trim() !== '' &&
    consumerInsight.trim() !== '' &&
    mainBenefit.trim() !== '' &&
    hasValidRTBs &&
    pricePoint.trim() !== '' &&
    packSize.trim() !== '' &&
    brand.trim() !== '' &&
    hasHeroImageOrDescription &&
    hasValidAlternatives;

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Filter out empty entries
      const filteredRTBs = reasonsTobelieve.filter(rtb => rtb.trim() !== '');
      const filteredAlternatives = alternativeProducts.filter(alt => alt.trim() !== '');

      const simulationData = {
        audience_id: audienceData.audienceId,
        segment_ids: selectedSegmentIds,
        persona_filters: personaFilters,
        name: simName || `Concept Test - ${headline}`,
        task: 'concept-test',
        headline,
        consumer_insight: consumerInsight,
        main_benefit: mainBenefit,
        reasons_to_believe: filteredRTBs,
        price_point: pricePoint,
        pack_size: packSize,
        brand,
        hero_image: {
          image: heroImageBase64,
          description: heroImageDescription || null
        },
        alternative_products: filteredAlternatives
      };

      const data = await createSimulation(simulationData);
      onSubmit(data.simulation_id);
    } catch (err) {
      console.error('Error starting simulation:', err);
      handleApiError(err, setError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
          <span className="text-sm text-gray-500">{selectedSegmentIds.length} segments selected</span>
        </div>

        {error && (
          <div className="text-sm text-red-600 mb-3 bg-red-50 p-3 rounded-md">
            {error}
          </div>
        )}
      </div>

      {/* Simulation Name */}
      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name (Optional)
        </label>
        <input
          id="simName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={simName}
          onChange={(e) => {
            setSimName(e.target.value);
            handleFormChange();
          }}
          placeholder="Enter a name for your simulation"
        />
      </div>

      {/* Headline */}
      <div className="mb-6">
        <label htmlFor="headline" className="block text-sm font-medium text-gray-700 mb-2">
          Product Headline <span className="text-red-500">*</span>
        </label>
        <input
          id="headline"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={headline}
          onChange={(e) => {
            setHeadline(e.target.value);
            handleFormChange();
          }}
          placeholder="e.g., Revolutionary Plant-Based Protein Snack"
        />
      </div>

      {/* Brand */}
      <div className="mb-6">
        <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-2">
          Brand Name <span className="text-red-500">*</span>
        </label>
        <input
          id="brand"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={brand}
          onChange={(e) => {
            setBrand(e.target.value);
            handleFormChange();
          }}
          placeholder="e.g., NatureBite"
        />
      </div>

      {/* Consumer Insight */}
      <div className="mb-6">
        <label htmlFor="consumerInsight" className="block text-sm font-medium text-gray-700 mb-2">
          Consumer Insight <span className="text-red-500">*</span>
        </label>
        <textarea
          id="consumerInsight"
          rows={3}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={consumerInsight}
          onChange={(e) => {
            setConsumerInsight(e.target.value);
            handleFormChange();
          }}
          placeholder="e.g., Busy professionals struggle to find healthy, convenient snacks that actually taste good"
        />
      </div>

      {/* Main Benefit */}
      <div className="mb-6">
        <label htmlFor="mainBenefit" className="block text-sm font-medium text-gray-700 mb-2">
          Main Benefit <span className="text-red-500">*</span>
        </label>
        <textarea
          id="mainBenefit"
          rows={2}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={mainBenefit}
          onChange={(e) => {
            setMainBenefit(e.target.value);
            handleFormChange();
          }}
          placeholder="e.g., Get 20g of protein in a delicious, ready-to-eat bar"
        />
      </div>

      {/* Reasons to Believe */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reasons to Believe <span className="text-red-500">*</span>
          <span className="text-gray-400 font-normal ml-2">(max 5)</span>
        </label>
        {reasonsTobelieve.map((rtb, index) => (
          <div key={index} className="flex items-center gap-2 mb-2">
            <input
              type="text"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={rtb}
              onChange={(e) => updateRTB(index, e.target.value)}
              placeholder={`RTB ${index + 1}: e.g., Made with organic pea protein`}
            />
            {reasonsTobelieve.length > 1 && (
              <button
                type="button"
                onClick={() => removeRTB(index)}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        {reasonsTobelieve.length < 5 && (
          <button
            type="button"
            onClick={addRTB}
            className="flex items-center text-sm text-blue-600 hover:text-blue-700 mt-2"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add another reason to believe
          </button>
        )}
      </div>

      {/* Price & Pack Size */}
      <div className="mb-6 grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="pricePoint" className="block text-sm font-medium text-gray-700 mb-2">
            Price Point <span className="text-red-500">*</span>
          </label>
          <input
            id="pricePoint"
            type="text"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={pricePoint}
            onChange={(e) => {
              setPricePoint(e.target.value);
              handleFormChange();
            }}
            placeholder="e.g., $4.99"
          />
        </div>
        <div>
          <label htmlFor="packSize" className="block text-sm font-medium text-gray-700 mb-2">
            Pack Size <span className="text-red-500">*</span>
          </label>
          <input
            id="packSize"
            type="text"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={packSize}
            onChange={(e) => {
              setPackSize(e.target.value);
              handleFormChange();
            }}
            placeholder="e.g., 2.5 oz bar"
          />
        </div>
      </div>

      {/* Hero Image */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Product Image <span className="text-red-500">*</span>
          <span className="text-gray-400 font-normal ml-2">(upload image or provide description)</span>
        </label>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Image Upload */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
            {heroImagePreview ? (
              <div className="relative">
                <img
                  src={heroImagePreview}
                  alt="Hero preview"
                  className="w-full h-48 object-contain rounded-md"
                />
                <button
                  type="button"
                  onClick={removeHeroImage}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center h-48 cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Click to upload image</span>
                <span className="text-xs text-gray-400 mt-1">Max 10MB</span>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          {/* Image Description */}
          <div>
            <label htmlFor="heroImageDescription" className="block text-sm text-gray-600 mb-2">
              Or describe the product image:
            </label>
            <textarea
              id="heroImageDescription"
              rows={6}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={heroImageDescription}
              onChange={(e) => {
                setHeroImageDescription(e.target.value);
                handleFormChange();
              }}
              placeholder="Describe the product packaging, appearance, and key visual elements..."
            />
          </div>
        </div>
        
        {!hasHeroImageOrDescription && (
          <p className="text-xs text-amber-600 mt-2">
            Please provide either an image or a description of your product
          </p>
        )}
      </div>

      {/* Alternative Products */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Competitor/Alternative Products <span className="text-red-500">*</span>
          <span className="text-gray-400 font-normal ml-2">(what the consumer currently uses)</span>
        </label>
        {alternativeProducts.map((alt, index) => (
          <div key={index} className="flex items-center gap-2 mb-2">
            <input
              type="text"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={alt}
              onChange={(e) => updateAlternativeProduct(index, e.target.value)}
              placeholder={`Alternative ${index + 1}: e.g., Clif Bar, Kind Bar`}
            />
            {alternativeProducts.length > 1 && (
              <button
                type="button"
                onClick={() => removeAlternativeProduct(index)}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        {alternativeProducts.length < 5 && (
          <button
            type="button"
            onClick={addAlternativeProduct}
            className="flex items-center text-sm text-blue-600 hover:text-blue-700 mt-2"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add another alternative
          </button>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          withArrow
        >
          {isSubmitting ? 'Running Concept Test...' : 'Run Concept Test'}
        </Button>
      </div>
    </div>
  );
};

export default ConceptTestForm;

